/**
 * SettingsPage — System · Settings editor.
 *
 * Wire contract derived from primary sources (2026-05-18):
 *   - Entity: PCBJxChangeApi/Data/Entities/Config/Codes/SystemSetting.cs
 *   - Backend route POST /v3/admin/system-settings:
 *       PCBJxChangeApi/Functions/AdminSystemFunction.cs:84
 *   - Wire-write DTO: SystemSettingUpsertInput in AdminSystemModels.cs:23
 *   - Live GET shape verified 2026-05-18 against
 *       /api/v3/config/system-settings/rows in prod:
 *       { success, data: [{ settingKey, settingValue, dataType, categoryCode,
 *           isSensitive, description, defaultValue, isActive,
 *           legacyAoConfigId, createdAt, createdBy, updatedAt, updatedBy }, ...] }
 *
 * NOT a port of apps/ROP-teams/src/modules/Admin/editors/systemSettingsEditor.tsx.
 * That editor uses ROP-teams's CrudPage + promptBypassKey() pattern and
 * carries an FSR-pipeline-specific Test Mode toggle that doesn't belong in
 * the admin SWA. This page is built from first principles on the canonical
 * admin-panel primitives (Card / DataTable / FormField / ModalShell / Pill /
 * Banner) and the MSAL-Path-B auth flow that other admin-panel pages already
 * use.
 *
 * Auth: the api-client's `client.retail.admin.systemSettings.upsert()` takes a
 * `bypassKey` parameter, but per `packages/api-client/src/admin-auth.ts` the
 * `ADMIN_AUTH_TOKEN_SENTINEL` value tells `buildAdminAuthHeaders` to OMIT
 * the bypass-key header so the Function-side AdminAuthService falls back to
 * the MSAL bearer Path B. That's the right path for admin-panel SWA per
 * memory `feedback_spfx_callers_need_anonymous_authlevel_20260516` +
 * `project_admin_auth_allowbypasskey_off_20260514` (bypass-key gated OFF
 * in prod).
 *
 * Shared-types `SystemSetting` interface at
 * `packages/shared-types/src/config/codes.ts:471` uses different field names
 * (`key`/`value`/`valueType`) that don't match the live wire shape — this
 * file declares its own row type that mirrors what the backend actually
 * returns. Until the shared-types interface is corrected, do NOT import it
 * here.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import {
  ADMIN_AUTH_TOKEN_SENTINEL,
  ApiError,
  type SystemSettingUpsertBody,
} from '@retail-ops/api-client';
import { useApiClient } from '@/contexts/ApiClientContext';
import { Banner, Button, Card, Pill, Spinner } from '@/components/primitives';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import {
  TextField,
  TextArea,
  SelectField,
  BoolField,
} from '@/components/FormField';
import { ModalShell } from '@/components/ModalShell';

interface SystemSettingRow {
  settingKey: string;
  settingValue: string | null;
  dataType: string;
  categoryCode: string;
  isSensitive: boolean;
  description: string | null;
  defaultValue: string | null;
  isActive: boolean;
  createdAt?: string | null;
  createdBy?: string | null;
  updatedAt?: string | null;
  updatedBy?: string | null;
}

interface EditDraft {
  isNew: boolean;
  settingKey: string;
  settingValue: string;
  dataType: string;
  categoryCode: string;
  description: string;
  defaultValue: string;
  isActive: boolean;
  isSensitive: boolean;
}

const DATA_TYPES = ['string', 'int', 'decimal', 'bool', 'json', 'url', 'duration'] as const;

function emptyDraft(): EditDraft {
  return {
    isNew: true,
    settingKey: '',
    settingValue: '',
    dataType: 'string',
    categoryCode: 'general',
    description: '',
    defaultValue: '',
    isActive: true,
    isSensitive: false,
  };
}

function rowToDraft(row: SystemSettingRow): EditDraft {
  return {
    isNew: false,
    settingKey: row.settingKey,
    settingValue: row.settingValue ?? '',
    dataType: row.dataType,
    categoryCode: row.categoryCode,
    description: row.description ?? '',
    defaultValue: row.defaultValue ?? '',
    isActive: row.isActive,
    isSensitive: row.isSensitive,
  };
}

function draftToUpsertBody(d: EditDraft): SystemSettingUpsertBody {
  return {
    settingKey: d.settingKey.trim(),
    settingValue: d.settingValue,
    dataType: d.dataType,
    categoryCode: d.categoryCode.trim() || 'general',
    isSensitive: d.isSensitive,
    description: d.description.trim() || null,
    defaultValue: d.defaultValue.trim() || null,
    isActive: d.isActive,
  };
}

function valuePreview(value: string | null, dataType: string): React.ReactNode {
  if (value === null || value === '') return <span className="cell-dim">—</span>;
  // Truncate long values (JSON blobs) for the row preview.
  const truncated = value.length > 64 ? value.slice(0, 64) + '…' : value;
  const className = dataType === 'json' || dataType === 'url' || dataType === 'string'
    ? 'cell-mono'
    : 'cell-mono--bold';
  return <span className={className}>{truncated}</span>;
}

function dataTypeBadge(dataType: string): React.ReactNode {
  const tone =
    dataType === 'bool' ? 'info' :
    dataType === 'json' ? 'muted' :
    dataType === 'url' ? 'muted' :
    'muted';
  return <Pill tone={tone}>{dataType}</Pill>;
}

export function SettingsPage(): JSX.Element {
  const client = useApiClient();
  const [rows, setRows] = useState<ReadonlyArray<SystemSettingRow> | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  // ?recent=1 (the Home tile "Recently changed settings") pre-arms the 30-day filter.
  const [searchParams] = useSearchParams();
  const [recentOnly, setRecentOnly] = useState(searchParams.get('recent') === '1');
  const RECENT_DAYS = 30;
  const [draft, setDraft] = useState<EditDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ tone: 'info' | 'warn'; text: string } | null>(null);
  const [refreshNonce, setRefreshNonce] = useState(0);

  // ── Load ───────────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setRows(null);
      setLoadError(null);
      try {
        const resp = await client.retail.config.systemSettingsRows();
        if (cancelled) return;
        if (resp.success && Array.isArray(resp.data)) {
          setRows(resp.data as unknown as ReadonlyArray<SystemSettingRow>);
        } else {
          setLoadError(resp.error ?? 'Failed to load system settings.');
        }
      } catch (e) {
        if (cancelled) return;
        setLoadError(
          ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e),
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, refreshNonce]);

  const refresh = useCallback(() => setRefreshNonce((n) => n + 1), []);

  // ── Filter + group ────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    if (!rows) return rows;
    const n = filter.trim().toLowerCase();
    const cutoff = Date.now() - RECENT_DAYS * 24 * 60 * 60 * 1000;
    return rows.filter((r) => {
      if (recentOnly) {
        const t = r.updatedAt ? Date.parse(r.updatedAt) : NaN;
        if (!Number.isFinite(t) || t < cutoff) return false;
      }
      if (!n) return true;
      const hay = `${r.settingKey} ${r.description ?? ''} ${r.categoryCode} ${r.settingValue ?? ''}`.toLowerCase();
      return hay.includes(n);
    });
  }, [rows, filter, recentOnly]);

  const groupedByCategory = useMemo(() => {
    if (!filtered) return null;
    const map = new Map<string, SystemSettingRow[]>();
    for (const r of filtered) {
      const list = map.get(r.categoryCode) ?? [];
      list.push(r);
      map.set(r.categoryCode, list);
    }
    for (const list of map.values()) {
      list.sort((a, b) => a.settingKey.localeCompare(b.settingKey));
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [filtered]);

  // ── Columns ────────────────────────────────────────────────────────────
  const columns: ReadonlyArray<DataTableColumn<SystemSettingRow>> = useMemo(
    () => [
      {
        header: 'Key',
        cell: (r) => <code className="cell-mono--bold">{r.settingKey}</code>,
      },
      {
        header: 'Value',
        cell: (r) => valuePreview(r.settingValue, r.dataType),
      },
      {
        header: 'Type',
        cell: (r) => dataTypeBadge(r.dataType),
      },
      {
        header: 'Description',
        cell: (r) =>
          r.description ? (
            <span className="cell-desc">{r.description}</span>
          ) : (
            <span className="cell-dim">—</span>
          ),
      },
      {
        header: 'Status',
        cell: (r) =>
          r.isActive ? <Pill tone="ok">active</Pill> : <Pill tone="muted">inactive</Pill>,
      },
      {
        header: '',
        cell: (r) => (
          <Button onClick={() => setDraft(rowToDraft(r))}>Edit</Button>
        ),
      },
    ],
    [],
  );

  // ── Save ───────────────────────────────────────────────────────────────
  const handleSave = useCallback(async () => {
    if (!draft) return;
    const trimmedKey = draft.settingKey.trim();
    if (!trimmedKey) {
      setSaveError('Setting key is required.');
      return;
    }
    if (!DATA_TYPES.includes(draft.dataType as typeof DATA_TYPES[number])) {
      setSaveError(`Data type must be one of: ${DATA_TYPES.join(', ')}.`);
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const resp = await client.retail.admin.systemSettings.upsert(
        ADMIN_AUTH_TOKEN_SENTINEL,
        draftToUpsertBody(draft),
      );
      if (!resp.success) {
        setSaveError(resp.error ?? 'admin upsert returned success=false');
        return;
      }
      setDraft(null);
      setToast({
        tone: 'info',
        text: draft.isNew
          ? `Created setting "${trimmedKey}".`
          : `Updated setting "${trimmedKey}".`,
      });
      refresh();
    } catch (e) {
      setSaveError(
        ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e),
      );
    } finally {
      setSaving(false);
    }
  }, [draft, client, refresh]);

  const handleDisable = useCallback(async () => {
    if (!draft || draft.isNew) return;
    const trimmedKey = draft.settingKey.trim();
    if (!trimmedKey) return;
    setSaving(true);
    setSaveError(null);
    try {
      const resp = await client.retail.admin.systemSettings.upsert(
        ADMIN_AUTH_TOKEN_SENTINEL,
        {
          settingKey: trimmedKey,
          isActive: false,
        },
      );
      if (!resp.success) {
        setSaveError(resp.error ?? 'soft-disable returned success=false');
        return;
      }
      setDraft(null);
      setToast({ tone: 'warn', text: `Disabled setting "${trimmedKey}". Row preserved for audit.` });
      refresh();
    } catch (e) {
      setSaveError(
        ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e),
      );
    } finally {
      setSaving(false);
    }
  }, [draft, client, refresh]);

  // ── Render ─────────────────────────────────────────────────────────────
  if (loadError) {
    return (
      <Banner tone="err">
        Could not load system settings: {loadError}{' '}
        <Button onClick={refresh}>Retry</Button>
      </Banner>
    );
  }
  if (!rows) return <Spinner label="Loading system settings…" />;

  const totalShown = filtered?.length ?? 0;

  return (
    <>
      {toast && (
        <Banner tone={toast.tone}>
          {toast.text}{' '}
          <Button onClick={() => setToast(null)}>Dismiss</Button>
        </Banner>
      )}

      <Card
        title={recentOnly
          ? `System Settings — changed in the last ${RECENT_DAYS} days — ${totalShown} of ${rows.length}`
          : `System Settings — ${totalShown} of ${rows.length}`}
        actions={
          <div className="card-actions-row">
            <div className="users-filter">
              <TextField
                label=""
                value={filter}
                onChange={setFilter}
                placeholder="Filter by key, description, category, or value…"
              />
            </div>
            <label className="settings-recent-toggle" data-tour="settings-recent">
              <input
                type="checkbox"
                checked={recentOnly}
                onChange={(e) => setRecentOnly(e.target.checked)}
              />{' '}
              Changed in the last {RECENT_DAYS} days
            </label>
            <Button variant="primary" onClick={() => setDraft(emptyDraft())}>
              + Add Setting
            </Button>
          </div>
        }
      >
        {groupedByCategory && groupedByCategory.length === 0 && (
          <p className="empty">No settings match the current filter.</p>
        )}
        {groupedByCategory?.map(([category, list]) => (
          <section key={category} className="settings-category">
            <h3 className="settings-category__heading">
              {category}
              <span className="settings-category__count">({list.length})</span>
            </h3>
            <DataTable<SystemSettingRow>
              columns={columns}
              rows={list}
              rowKey={(r) => r.settingKey}
            />
          </section>
        ))}
      </Card>

      {/* ── Edit / Add modal ──────────────────────────────────────────── */}
      <ModalShell
        open={draft !== null}
        title={draft?.isNew ? 'Add System Setting' : `Edit ${draft?.settingKey ?? ''}`}
        onClose={() => {
          if (saving) return;
          setDraft(null);
          setSaveError(null);
        }}
        width={620}
        actions={
          draft && (
            <>
              <Button onClick={() => setDraft(null)} disabled={saving}>
                Cancel
              </Button>
              {!draft.isNew && draft.isActive && (
                <Button onClick={() => void handleDisable()} disabled={saving}>
                  Disable
                </Button>
              )}
              <Button variant="primary" onClick={() => void handleSave()} disabled={saving}>
                {saving ? 'Saving…' : draft.isNew ? 'Create' : 'Save changes'}
              </Button>
            </>
          )
        }
      >
        {draft && (
          <div className="form-stack">
            {saveError && <Banner tone="err">{saveError}</Banner>}

            <TextField
              label="Setting key"
              value={draft.settingKey}
              onChange={(v) => setDraft({ ...draft, settingKey: v })}
              placeholder="e.g. notifications.emailSenderUpn"
              disabled={!draft.isNew}
              hint={
                draft.isNew
                  ? 'Permanent identifier. Use a dotted namespace: <area>.<facet>.<thing>.'
                  : 'The key is the primary identifier — it cannot be renamed. To rename, disable this row and create a new one.'
              }
            />

            <SelectField
              label="Data type"
              value={draft.dataType}
              onChange={(v) => setDraft({ ...draft, dataType: v })}
              options={DATA_TYPES.map((t) => ({ value: t, label: t }))}
              hint="Drives how readers parse settingValue. 'bool' accepts true/false/1/0/yes/no/on/off."
            />

            <TextField
              label="Category"
              value={draft.categoryCode}
              onChange={(v) => setDraft({ ...draft, categoryCode: v })}
              placeholder="e.g. notifications, identity, fraud, jxchange"
              hint="Groups related rows in the list view. Free-form string."
            />

            <TextArea
              label="Value"
              value={draft.settingValue}
              onChange={(v) => setDraft({ ...draft, settingValue: v })}
              rows={draft.dataType === 'json' ? 8 : 3}
              placeholder={draft.dataType === 'json' ? '{\n  "key": "value"\n}' : ''}
              hint={
                draft.isSensitive
                  ? 'This setting is marked sensitive. The value above may be a Key Vault reference.'
                  : undefined
              }
            />

            <TextArea
              label="Description"
              value={draft.description}
              onChange={(v) => setDraft({ ...draft, description: v })}
              rows={2}
              placeholder="One-sentence explanation of what this setting controls."
            />

            <TextField
              label="Default value"
              value={draft.defaultValue}
              onChange={(v) => setDraft({ ...draft, defaultValue: v })}
              placeholder="Fallback used by callers when the row is absent or inactive."
            />

            <BoolField
              label="Active"
              value={draft.isActive}
              onChange={(v) => setDraft({ ...draft, isActive: v })}
              hint="When inactive, code reading this setting falls back to the default value or hard-coded behavior. Row stays for audit."
            />

            <BoolField
              label="Sensitive"
              value={draft.isSensitive}
              onChange={(v) => setDraft({ ...draft, isSensitive: v })}
              hint="Mark true if the value is a credential, token, or Key Vault reference. Sensitive rows are filtered out of some surfaces."
            />
          </div>
        )}
      </ModalShell>
    </>
  );
}
